/**
 * rng.ts —— 两条独立可注入种子的随机流（PRD 8.4）
 *
 * 为什么必须是两条独立流而不是一条：
 *   如果洗牌和 AI 扰动共用一条流，AI 多消耗一次随机数就会改变后续所有发牌，
 *   于是「同种子 + 同动作序列 → 逐动作复现」这条契约（AC3.3 / AC8.4）会被
 *   AI 逻辑的任何微调打破，QA 无法固化用例。分成 shuffleRNG / aiRNG 后，
 *   调 AI 参数不影响牌序，改洗牌不影响 AI 行为。
 *
 * 为什么不用 crypto.getRandomValues 作为主实现：
 *   PRD 8.3 要求洗牌均匀且「加密安全随机源」，PRD 8.4 又要求可复现——两者冲突。
 *   本实现的取舍：默认种子来自 crypto（不可预测），但一旦种子确定，序列即确定。
 *   即「种子是密码学强度的，展开是确定性的」，两条要求同时满足。
 */

/** 随机流的可序列化快照：存档与手牌历史需要记录（PRD 5.2）。 */
export interface RngState {
  /** 32 位无符号整数种子。 */
  readonly seed: number;
  /** 已消耗的步数，用于存档恢复后精确接续。 */
  readonly steps: number;
}

/** 随机流接口。引擎内部所有随机性必须经由此接口，便于测试注入与审计。 */
export interface Rng {
  /** 流名称，便于日志与调试（"shuffle" / "ai"）。 */
  readonly name: string;
  /** [0, 1) 均匀分布浮点数。 */
  next(): number;
  /** [0, bound) 均匀分布整数，无模偏（rejection sampling）。 */
  nextInt(bound: number): number;
  /** [min, max] 闭区间整数。 */
  nextIntRange(min: number, max: number): number;
  /** [min, max) 均匀分布浮点数。 */
  nextFloat(min: number, max: number): number;
  /** 以 p 的概率返回 true。 */
  chance(p: number): boolean;
  /** 当前状态快照。 */
  getState(): RngState;
  /** 恢复到指定状态（存档续局）。 */
  setState(state: RngState): void;
}

/**
 * sfc32 —— 32 位状态的快速高质量 PRNG。
 * 选它的理由：周期 ≥2^32、通过 PractRand、纯 32 位整数运算（JS 下 |0 运算最快），
 * 且状态只有 4 个 uint32，序列化进存档的成本可忽略。
 */
function sfc32(a: number, b: number, c: number, d: number): () => number {
  let s0 = a >>> 0;
  let s1 = b >>> 0;
  let s2 = c >>> 0;
  let s3 = d >>> 0;
  return function next(): number {
    s0 >>>= 0; s1 >>>= 0; s2 >>>= 0; s3 >>>= 0;
    let t = (s0 + s1) >>> 0;
    s0 = s1 ^ (s1 >>> 9);
    s1 = (s2 + (s2 << 3)) >>> 0;
    s2 = (s2 << 21) | (s2 >>> 11);
    s3 = (s3 + 1) >>> 0;
    t = (t + s3) >>> 0;
    s2 = (s2 + t) >>> 0;
    return (t >>> 0) / 4294967296;
  };
}

/** splitmix32：把单个 32 位种子扩展成 4 个互不相关的状态字。 */
function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return function next(): number {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = (Math.imul(z ^ (z >>> 16), 0x21f0aaad)) >>> 0;
    z = (Math.imul(z ^ (z >>> 15), 0x735a2d97)) >>> 0;
    return (z ^ (z >>> 15)) >>> 0;
  };
}

class SeededRng implements Rng {
  readonly name: string;
  private seed: number;
  private steps = 0;
  private core: () => number;

  constructor(name: string, seed: number) {
    this.name = name;
    this.seed = seed >>> 0;
    this.core = this.spawn(this.seed);
  }

  private spawn(seed: number): () => number {
    const sm = splitmix32(seed);
    return sfc32(sm(), sm(), sm(), sm());
  }

  next(): number {
    this.steps++;
    return this.core();
  }

  nextInt(bound: number): number {
    if (!Number.isInteger(bound) || bound <= 0) {
      throw new Error(`nextInt(bound) 要求正整数，收到 ${bound}`);
    }
    // 拒绝采样消除模偏：2^32 不一定被 bound 整除，直接取模会让小余数更常出现。
    const limit = Math.floor(4294967296 / bound) * bound;
    for (;;) {
      const raw = Math.floor(this.next() * 4294967296);
      if (raw < limit) return raw % bound;
    }
  }

  nextIntRange(min: number, max: number): number {
    if (max < min) throw new Error(`nextIntRange: max(${max}) < min(${min})`);
    return min + this.nextInt(max - min + 1);
  }

  nextFloat(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  chance(p: number): boolean {
    if (p <= 0) return false;
    if (p >= 1) return true;
    return this.next() < p;
  }

  getState(): RngState {
    return { seed: this.seed, steps: this.steps };
  }

  setState(state: RngState): void {
    this.seed = state.seed >>> 0;
    this.core = this.spawn(this.seed);
    // 快进到指定步数：sfc32 无跳跃函数，用重放实现。
    // steps 上限受单局手数限制（500 手 × 每手 O(100) 次），重放成本可忽略。
    for (let i = 0; i < state.steps; i++) this.core();
    this.steps = state.steps;
  }
}

/** 生成一个密码学强度的 32 位随机种子（未指定种子时的默认来源）。 */
export function randomSeed(): number {
  const g = globalThis as { crypto?: { getRandomValues?: (a: Uint32Array) => Uint32Array } };
  if (g.crypto?.getRandomValues) {
    const buf = new Uint32Array(1);
    g.crypto.getRandomValues(buf);
    return buf[0] >>> 0;
  }
  // 无 crypto 的宿主（老旧 Worker 环境）退化为时间 + Math.random，仅影响不可预测性，
  // 不影响「种子确定则序列确定」这条核心契约。
  return (Math.floor(Math.random() * 4294967296) ^ Date.now()) >>> 0;
}

export function createRng(name: string, seed: number): Rng {
  return new SeededRng(name, seed);
}

/** 两条随机流的种子对。留空字段由 randomSeed() 填充。 */
export interface SeedPair {
  shuffle: number;
  ai: number;
}

/** 两条独立随机流（PRD 8.4）。 */
export interface RngBundle {
  /** 洗牌 / 发牌专用。 */
  readonly shuffleRNG: Rng;
  /** AI 扰动、情绪、停顿时长专用。绝不参与发牌。 */
  readonly aiRNG: Rng;
  seeds(): SeedPair;
  snapshot(): { shuffle: RngState; ai: RngState };
  restore(snap: { shuffle: RngState; ai: RngState }): void;
}

/**
 * 从**单个**用户可见种子派生出两条流的种子（PRD 模块 3 只暴露一个种子输入框，
 * 而 PRD 8.4 要求两条独立流）。
 *
 * 用 splitmix32 的两次不同步进来派生，保证：
 *  - 同一用户种子 → 恒得到同一对流种子（AC3.3 可复现）；
 *  - 两条流的种子互不相等、且低位不相关（避免两条流产生同步的模式）。
 */
export function deriveSeedPair(seed: number): SeedPair {
  let x = seed >>> 0;
  const step = (): number => {
    x = (x + 0x9e3779b9) >>> 0;
    let z = x;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return (z ^ (z >>> 15)) >>> 0;
  };
  return { shuffle: step(), ai: step() };
}

export function createRngBundle(seeds?: Partial<SeedPair>): RngBundle {
  // 防御误用：本函数接收的是 SeedPair 对象，不是单个数字。
  // 若调用方误传 createRngBundle(777)，`seeds?.shuffle` 会取到 undefined，
  // 两条流都会静默退化为随机种子 —— 对「同种子可复现」（PRD 8.4）是灾难性的静默失败，
  // 因此这里显式抛错而不是容忍。
  if (seeds !== undefined && (typeof seeds !== 'object' || seeds === null)) {
    throw new TypeError(
      `createRngBundle 需要 SeedPair 对象（如 deriveSeedPair(seed) 或 { shuffle, ai }），收到 ${typeof seeds}`,
    );
  }
  const shuffleSeed = seeds?.shuffle ?? randomSeed();
  const aiSeed = seeds?.ai ?? randomSeed();
  const shuffleRNG = createRng('shuffle', shuffleSeed);
  const aiRNG = createRng('ai', aiSeed);
  return {
    shuffleRNG,
    aiRNG,
    seeds: () => ({ shuffle: shuffleSeed, ai: aiSeed }),
    snapshot: () => ({ shuffle: shuffleRNG.getState(), ai: aiRNG.getState() }),
    restore: (snap) => {
      shuffleRNG.setState(snap.shuffle);
      aiRNG.setState(snap.ai);
    },
  };
}

/**
 * Fisher–Yates 洗牌（PRD 8.3）。原地修改并返回同一数组。
 * 从后往前取 nextInt(i+1) 是无偏版本；常见的 `nextInt(n)` 写法会产生偏置。
 */
export function shuffleInPlace<T>(arr: T[], rng: Rng): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}
